import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { EmptyStateComponent } from '../../../components/ui/empty-state/empty-state.component';
import { InspectionReportDialogComponent } from '../../../components/ui/inspection-report/inspection-report-dialog.component';
import { InspectionScopeSummaryComponent } from '../../../components/ui/inspection-scope/inspection-scope-summary.component';
import { CalendarRange, SessionsCalendarComponent } from '../../../components/ui/sessions-calendar/sessions-calendar.component';
import { MaterialPreviewComponent, PreviewItem } from '../../../components/ui/views/material-preview/material-preview.component';
import { SessionAudienceComponent } from '../../../components/ui/session-audience/session-audience.component';
import { AcademicSchedulingOptions, SchedulingSubject, displayTime, sessionStart } from '../../../core/interfaces/academic-appointment';
import { InspectableSession } from '../../../core/interfaces/inspection';
import { InspectionScope, isEmptyScope } from '../../../core/interfaces/inspection-scope';
import { SessionJoinService } from '../../../core/services/academic-appointments/session-join.service';
import { SessionViewService } from '../../../core/services/academic-appointments/session-view.service';
import { InspectionRulesService } from '../../../core/services/inspection-rules.service';
import { InspectionService } from '../../../core/services/inspection.service';
import { SubjectChoiceService, SubjectPathGroup } from '../../../core/services/utils/subject-choice.service';

import { LocalizedDatePipe } from '../../../shared/pipes/localized-date.pipe';
/**
 * L'agenda de l'inspecteur : les séances de son périmètre, dans la grille que
 * l'administration utilise, avec l'état d'inspection sur chaque pastille.
 * Une séance s'ouvre dans un tiroir — rejoindre, consulter l'enregistrement,
 * rédiger le rapport — sans quitter l'agenda.
 */
@Component({
  selector: 'app-inspector-calendar',
  standalone: true,
  imports: [
    LocalizedDatePipe,
    DatePipe,
    FormsModule,
    TranslateModule,
    EmptyStateComponent,
    InspectionReportDialogComponent,
    InspectionScopeSummaryComponent,
    SessionsCalendarComponent,
    MaterialPreviewComponent,
    SessionAudienceComponent,
  ],
  templateUrl: './inspector-calendar.component.html',
  styleUrl: './inspector-calendar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectorCalendarComponent implements OnInit {
  private readonly service = inject(InspectionService);
  private readonly joinService = inject(SessionJoinService);
  private readonly subjectChoice = inject(SubjectChoiceService);
  readonly rules = inject(InspectionRulesService);
  readonly view = inject(SessionViewService);

  @ViewChild('detailDialog') private detailDialog?: ElementRef<HTMLDialogElement>;

  readonly sessions = signal<InspectableSession[]>([]);
  readonly loading = signal(false);
  readonly errored = signal(false);

  readonly options = signal<AcademicSchedulingOptions>({ subjects: [], groups: [], students: [], professors: [] });
  readonly scope = signal<InspectionScope | null>(null);
  readonly scopeIsEmpty = computed(() => this.scope() !== null && isEmptyScope(this.scope()));

  // ── Facettes ───────────────────────────────────────────────
  filterProfessorId: number | null = null;
  filterSubjectId: number | null = null;
  /** Ne montrer que ce qui reste à inspecter — l'agenda complet par défaut, avec l'état sur chaque séance. */
  onlyTodo = false;

  /**
   * Des filtres sont posés. Un accesseur, pas un `computed` : les champs liés
   * par ngModel ne sont pas des signaux, un `computed` n'aurait aucun
   * producteur et resterait figé à sa première valeur.
   */
  get hasFilters(): boolean {
    return this.filterProfessorId !== null || this.filterSubjectId !== null || this.onlyTodo;
  }

  readonly subjectGroups = computed<SubjectPathGroup[]>(() =>
    this.subjectChoice.groupByPath(this.options().subjects),
  );

  /** La période que la grille affiche — ce qu'on recharge quand un filtre change. */
  private range: CalendarRange | null = null;

  // ── Tiroir, rapport, enregistrement ────────────────────────
  readonly selected = signal<InspectableSession | null>(null);
  readonly target = signal<InspectableSession | null>(null);
  readonly preview = signal<PreviewItem | null>(null);
  readonly joinError = signal<string | null>(null);

  ngOnInit(): void {
    this.loadReferential();
  }

  // ── Période et filtres ─────────────────────────────────────

  onRange(range: CalendarRange): void {
    this.range = range;
    this.load();
  }

  applyFilters(): void {
    this.load();
  }

  clearFilters(): void {
    this.filterProfessorId = null;
    this.filterSubjectId = null;
    this.onlyTodo = false;
    this.load();
  }

  reload(): void {
    this.loadReferential();
    this.load();
  }

  /** Public : le dialogue de rapport demande une relecture quand la base a changé. */
  load(): void {
    if (!this.range) return;
    this.loading.set(true);
    this.errored.set(false);

    this.service
      .calendar({
        from: this.range.from,
        to: this.range.to,
        professorId: this.filterProfessorId,
        subjectId: this.filterSubjectId,
        inspected: this.onlyTodo ? false : null,
      })
      .subscribe({
        next: (res) => {
          const list = res?.data ?? [];
          this.sessions.set(list);
          // La fiche ouverte lit la séance relue : une pièce retirée depuis le
          // dialogue n'y réapparaît pas, un rapport déposé s'y trouve.
          const open = this.selected();
          if (open) this.selected.set(list.find((s) => s.id === open.id) ?? open);
          this.loading.set(false);
        },
        error: () => {
          // Aucune séance de substitution : la grille reste vide.
          this.sessions.set([]);
          this.errored.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadReferential(): void {
    this.service.options().subscribe({
      next: (res) => {
        if (!res?.data) return;
        this.options.set({ subjects: res.data.subjects, professors: res.data.professors, groups: [], students: [] });
        this.scope.set(res.data.scope ?? { all: false, items: [] });
      },
      // Sans référentiel l'agenda reste consultable : seuls les filtres perdent
      // leurs listes, et le périmètre reste inconnu — pas « vide ».
      error: () => {
        this.options.set({ subjects: [], groups: [], students: [], professors: [] });
        this.scope.set(null);
      },
    });
  }

  // ── Tiroir ─────────────────────────────────────────────────

  /** La grille rend la séance telle qu'elle l'a reçue : on retrouve la fiche complète. */
  onSelect(picked: { id: number }): void {
    const full = this.sessions().find((s) => s.id === picked.id);
    if (!full) return;
    this.selected.set(full);
    this.joinError.set(null);
    // `<dialog>` + showModal() : peint dans la couche supérieure, hors de tout
    // contexte d'empilement de la coquille.
    const el = this.detailDialog?.nativeElement;
    if (el && !el.open) el.showModal();
  }

  closeDetail(): void {
    this.detailDialog?.nativeElement.close();
  }

  /** Reflète dans l'état la fermeture faite par le navigateur (Échap, close()). */
  onDetailClosed(): void {
    this.selected.set(null);
  }

  private pressOrigin: EventTarget | null = null;

  onPressStart(event: MouseEvent): void {
    this.pressOrigin = event.target;
  }

  /**
   * Un clic sur le fond d'un `<dialog>` a pour cible le dialogue lui-même :
   * on exige que le geste ait commencé sur le fond, pour qu'une sélection de
   * texte relâchée en dehors du tiroir ne le referme pas.
   */
  onBackdropClick(event: MouseEvent, dialog: HTMLDialogElement): void {
    const startedOnBackdrop = this.pressOrigin === dialog || this.pressOrigin === null;
    this.pressOrigin = null;
    if (event.target === dialog && startedOnBackdrop) dialog.close();
  }

  // ── Actes depuis la fiche ──────────────────────────────────

  openReport(s: InspectableSession): void {
    this.target.set(s);
  }

  /** Le rapport est déposé : la grille se relit, la pastille change d'état. */
  onReportSaved(): void {
    this.target.set(null);
    this.closeDetail();
    this.load();
  }

  join(s: InspectableSession): void {
    this.joinError.set(null);
    this.joinService.openMeeting(s.id, (err) => this.joinError.set(messageFrom(err)));
  }

  /** L'enregistrement se lit dans l'aperçu, par son URL signée servie en ligne — jamais un lien qui télécharge. */
  openRecording(s: InspectableSession): void {
    const target = this.rules.playable(s);
    if (!target) return;
    this.preview.set({
      title: target.title || s.title,
      category: target.category,
      meta: [s.consultantName, this.subjectsOf(s)].filter((x): x is string => !!x),
      url: target.previewUrl,
      externalUrl: target.previewUrl ? null : target.externalUrl,
    });
  }

  // ── Helpers de gabarit ─────────────────────────────────────

  subjectLabel(s: SchedulingSubject): string {
    return this.subjectChoice.label(s, this.options().subjects);
  }

  subjectsOf(s: InspectableSession): string {
    return this.subjectChoice.subjectsOf(s);
  }

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: InspectableSession): string {
    return sessionStart(s);
  }
}

/** Le message du serveur, lisible par l'utilisateur ; sinon le libellé — jamais un texte technique. */
function messageFrom(err: unknown): string {
  const e = err as { error?: { message?: string } };
  return e?.error?.message || 'INSPECTION.SAVE_ERROR';
}
